import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, collection, getDocs, addDoc, query, where, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import Confetti from 'react-confetti';
import { useAuth } from '../context/AuthContext';
import { db, functions } from '../config/firebase';
import MediaGallery from '../components/MediaGallery';
import MarkdownText from '../components/MarkdownText';
import { formatCurrency } from '../utils/currency';

export default function RaffleDetail() {
  const params = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Determine if we're using slug (/rifa/:slug) or ID (/raffle/:id)
  const isSlugRoute = location.pathname.startsWith('/rifa/');
  const paramValue = params.slug || params.id;

  const [raffleId, setRaffleId] = useState(isSlugRoute ? null : paramValue);
  const [raffle, setRaffle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [numberStates, setNumberStates] = useState({});
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [pixCopied, setPixCopied] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportData, setReportData] = useState({ reason: '', details: '' });
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportSent, setReportSent] = useState(false);

  // Buyer email + comprovante
  const [buyerEmail, setBuyerEmail] = useState('');
  const [comprovanteFile, setComprovanteFile] = useState(null);
  const [comprovantePreview, setComprovantePreview] = useState(null);
  const fileInputRef = useRef(null);

  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Pre-fill email from currentUser
  useEffect(() => {
    if (currentUser?.email) {
      setBuyerEmail(currentUser.email);
    }
  }, [currentUser]);

  // Fetch raffle by slug or ID
  useEffect(() => {
    const fetchRaffle = async () => {
      try {
        let resolvedId = raffleId;
        let raffleData = null;

        if (isSlugRoute && !resolvedId) {
          // Look up by slug
          const slugQuery = query(
            collection(db, 'raffles'),
            where('slug', '==', paramValue),
            limit(1)
          );
          const slugSnap = await getDocs(slugQuery);
          if (!slugSnap.empty) {
            const docSnap = slugSnap.docs[0];
            resolvedId = docSnap.id;
            raffleData = { id: docSnap.id, ...docSnap.data() };
            setRaffleId(resolvedId);
          } else {
            // Try as ID fallback
            const docRef = doc(db, 'raffles', paramValue);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
              resolvedId = docSnap.id;
              raffleData = { id: docSnap.id, ...docSnap.data() };
              setRaffleId(resolvedId);
            }
          }
        } else if (resolvedId) {
          const docRef = doc(db, 'raffles', resolvedId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            raffleData = { id: docSnap.id, ...docSnap.data() };
          }
        }

        if (raffleData) {
          setRaffle(raffleData);

          const ticketsRef = collection(db, 'raffles', raffleData.id, 'tickets');
          const ticketsSnap = await getDocs(ticketsRef);
          const states = {};
          const maxNum = raffleData.numberType === 'unlimited' ? 100 : raffleData.maxNumbers;

          for (let i = 1; i <= maxNum; i++) {
            states[i] = 'available';
          }

          ticketsSnap.docs.forEach(ticketDoc => {
            const ticket = ticketDoc.data();
            const num = ticket.number;
            if (num && states[num] !== undefined) {
              if (ticket.status === 'confirmed') {
                states[num] = 'sold';
              } else if (['pending', 'awaiting_payment'].includes(ticket.status)) {
                states[num] = 'reserved';
              }
            }
          });

          setNumberStates(states);
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error('Error fetching raffle:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    if (paramValue) fetchRaffle();
  }, [paramValue, isSlugRoute, raffleId]);

  useEffect(() => {
    const handleResize = () => setWindowDimensions({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleNumberClick = (number) => {
    if (numberStates[number] === 'sold' || numberStates[number] === 'reserved') return;

    setSelectedNumbers(prev =>
      prev.includes(number) ? prev.filter(n => n !== number) : [...prev, number]
    );
    setNumberStates(prev => ({
      ...prev,
      [number]: prev[number] === 'selected' ? 'available' : 'selected'
    }));
  };

  // Open PIX payment modal — no login required
  const handleBuyWithPix = () => {
    if (selectedNumbers.length === 0) return alert('Selecione pelo menos um número');
    setShowPaymentModal(true);
    setPixCopied(false);
    setComprovanteFile(null);
    setComprovantePreview(null);
  };

  const handleCopyPixKey = () => {
    const textToCopy = raffle.pixKeyType === 'copiaecola' ? raffle.pixKey : raffle.pixKey;
    navigator.clipboard.writeText(textToCopy);
    setPixCopied(true);
    setTimeout(() => setPixCopied(false), 3000);
  };

  const handleComprovanteChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Arquivo muito grande. Máximo 5MB.');
        return;
      }
      setComprovanteFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setComprovantePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleConfirmPixSent = async () => {
    if (!buyerEmail || !buyerEmail.includes('@')) {
      alert('Por favor, informe um e-mail válido.');
      return;
    }
    if (!comprovanteFile) {
      alert('Por favor, envie o comprovante de pagamento.');
      return;
    }

    setPurchasing(true);
    try {
      const comprovanteBase64 = await fileToBase64(comprovanteFile);

      const reserveTickets = httpsCallable(functions, 'reserve_pix_tickets');
      const result = await reserveTickets({
        raffleId: raffleId,
        selectedNumbers,
        buyerEmail,
        buyerName: currentUser?.displayName || buyerEmail.split('@')[0],
        comprovanteBase64,
        comprovanteFilename: comprovanteFile.name,
      });

      if (result.data.success) {
        setShowPaymentModal(false);
        setPaymentSuccess(true);
        setShowConfetti(true);

        setTimeout(() => {
          setShowConfetti(false);
          const newStates = { ...numberStates };
          selectedNumbers.forEach(num => { newStates[num] = 'reserved'; });
          setNumberStates(newStates);
          setSelectedNumbers([]);
        }, 4000);
      }
    } catch (error) {
      console.error('Error:', error);
      alert(error.message || 'Erro ao reservar bilhetes. Tente novamente.');
    } finally {
      setPurchasing(false);
    }
  };

  const handleShareRaffle = () => {
    const slug = raffle.slug || raffleId;
    const url = window.location.origin + '/rifa/' + slug;
    if (navigator.share) {
      navigator.share({ title: raffle.title, url });
    } else {
      navigator.clipboard.writeText(url);
      alert('Link copiado!');
    }
  };

  const handleReportSubmit = async () => {
    if (!reportData.reason) return;
    setReportSubmitting(true);
    try {
      const reportRef = collection(db, 'reports');
      await addDoc(reportRef, {
        raffleId: raffleId,
        raffleTitle: raffle.title,
        organizerId: raffle.createdBy,
        organizerEmail: raffle.createdByEmail,
        reporterEmail: currentUser?.email || 'anonymous',
        reporterUid: currentUser?.uid || null,
        reason: reportData.reason,
        details: reportData.details,
        status: 'pending',
        createdAt: new Date().toISOString(),
      });
      setReportSent(true);
      setTimeout(() => { setShowReportModal(false); setReportSent(false); setReportData({ reason: '', details: '' }); }, 3000);
    } catch (err) {
      console.error('Error submitting report:', err);
      alert(t('legal.reportFailed'));
    } finally {
      setReportSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', fontSize: '2rem' }}>
        {t('common.loading')}
      </div>
    );
  }

  if (notFound || !raffle) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '400px', textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>😔</div>
        <h2>Rifa não encontrada</h2>
      </div>
    );
  }

  const totalCost = selectedNumbers.length * raffle.ticketPrice;
  const cur = raffle.currency || 'BRL';
  const fmtPrice = formatCurrency(raffle.ticketPrice, cur);
  const fmtTotal = formatCurrency(totalCost, cur);
  const maxNum = raffle.numberType === 'unlimited' ? 100 : raffle.maxNumbers;
  const soldCount = Object.values(numberStates).filter(s => s === 'sold').length;
  const reservedCount = Object.values(numberStates).filter(s => s === 'reserved').length;
  const progressPercent = raffle.numberType === 'limited'
    ? ((soldCount + reservedCount) / maxNum) * 100
    : (raffle.numbersSold / 100) * 100;

  // Only show stats to the raffle owner
  const isOwner = currentUser && currentUser.uid === raffle.createdBy;

  // Check if draw date has passed
  const isExpired = raffle.drawDate && new Date(raffle.drawDate) < new Date();
  const isAwaitingDraw = isExpired && raffle.status === 'active' && !raffle.winnerNumber;
  const canPurchase = raffle.status === 'active' && !isExpired;

  return (
    <>
      {showConfetti && (
        <div className="confetti-wrapper">
          <Confetti width={windowDimensions.width} height={windowDimensions.height} numberOfPieces={200} recycle={false} />
        </div>
      )}

      <div className="raffle-detail">
        {/* Payment Success Banner */}
        {paymentSuccess && (
          <div style={{
            background: 'linear-gradient(135deg, #00b894, #00cec9)',
            borderRadius: 'var(--radius-lg)', padding: '2rem', textAlign: 'center',
            marginBottom: '2rem', color: '#fff', boxShadow: 'var(--shadow-lg)'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🎉</div>
            <h2 style={{ margin: '0 0 0.5rem' }}>Bilhetes Reservados!</h2>
            <p style={{ fontSize: '1rem', opacity: 0.9 }}>
              Seu comprovante foi enviado. O criador da rifa vai verificar e confirmar seu pagamento. Você receberá um e-mail com a atualização!
            </p>
          </div>
        )}

        {/* Winner Banner */}
        {raffle.status === 'completed' && raffle.winnerNumber && (
          <div style={{
            background: 'linear-gradient(135deg, #f6d365, #fda085)',
            borderRadius: 'var(--radius-lg)', padding: '2rem', textAlign: 'center',
            marginBottom: '2rem', color: '#fff', boxShadow: 'var(--shadow-lg)'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🏆</div>
            <h2 style={{ margin: '0 0 0.5rem', textAlign: 'center' }}>Sorteio Realizado!</h2>
            <div style={{
              background: 'rgba(255,255,255,0.2)', borderRadius: 'var(--radius-md)',
              padding: '1rem', margin: '0.5rem auto', maxWidth: '300px',
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 800 }}>Nº {raffle.winnerNumber}</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 600, marginTop: '0.25rem' }}>{raffle.winnerName}</div>
            </div>
            {raffle.drawProofUrl && (
              <a href={raffle.drawProofUrl} target="_blank" rel="noopener noreferrer"
                style={{ color: '#fff', fontSize: '0.9rem', textDecoration: 'underline', opacity: 0.9, display: 'inline-block', marginTop: '0.75rem' }}>
                📎 Ver Comprovante do Sorteio
              </a>
            )}
          </div>
        )}

        {/* Awaiting Draw Banner */}
        {isAwaitingDraw && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(253,203,110,0.2), rgba(225,112,85,0.2))',
            borderRadius: 'var(--radius-lg)', padding: '2rem', textAlign: 'center',
            marginBottom: '2rem', border: '2px solid #fdcb6e', boxShadow: 'var(--shadow-md)'
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>⏳</div>
            <h2 style={{ margin: '0 0 0.5rem', color: 'var(--dark)', textAlign: 'center' }}>Aguardando Sorteio</h2>
            <p style={{ color: 'var(--dark-light)', margin: 0, textAlign: 'center' }}>
              O período de vendas encerrou. O resultado do sorteio será publicado em breve.
            </p>
          </div>
        )}

        {/* Media Gallery (images + videos) */}
        <MediaGallery
          media={raffle.mediaGallery || []}
          imageUrl={raffle.imageUrl}
        />

        {/* Header */}
        <div className="raffle-detail-header">
          {!raffle.imageUrl && (!raffle.mediaGallery || raffle.mediaGallery.length === 0) && (
            <div className="raffle-detail-image">
              <span style={{ fontSize: '4rem' }}>🎰</span>
            </div>
          )}
          <div className="raffle-detail-info">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
              <h1 style={{ margin: 0, flex: 1 }}>{raffle.title}</h1>
              {isOwner && (
                <Link
                  to={`/manage/${raffleId}`}
                  style={{
                    textDecoration: 'none',
                    background: 'linear-gradient(135deg, #6C5CE7, #00CEC9)',
                    color: 'white', border: 'none', fontWeight: 700,
                    padding: '0.6rem 1.4rem', borderRadius: 'var(--radius-md)',
                    fontSize: '0.9rem', letterSpacing: '0.02em',
                    boxShadow: '0 4px 15px rgba(108, 92, 231, 0.3)',
                    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                    transition: 'all 0.3s ease',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(108, 92, 231, 0.4)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(108, 92, 231, 0.3)';
                  }}
                >
                  <span style={{ fontSize: '1.1rem' }}>⚙️</span> Gerenciar Rifa
                </Link>
              )}
            </div>
            <MarkdownText
              text={raffle.description}
              style={{ marginBottom: '1.5rem', color: 'var(--dark-light)', fontSize: '0.95rem' }}
            />

            <div className="raffle-detail-stats">
              <div className="stat-card">
                <div className="stat-label">{t('raffle.ticketPrice')}</div>
                <div className="stat-value">{fmtPrice}</div>
              </div>
              {isOwner && (
                <div className="stat-card">
                  <div className="stat-label">{t('raffle.totalNumbers')}</div>
                  <div className="stat-value">{maxNum}</div>
                </div>
              )}
              {isOwner && (
                <div className="stat-card">
                  <div className="stat-label">{t('raffle.sold')}</div>
                  <div className="stat-value">{soldCount}</div>
                </div>
              )}
              <div className="stat-card" style={{ position: 'relative', overflow: 'hidden' }}>
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
                  background: 'linear-gradient(90deg, var(--primary), var(--secondary))',
                }}></div>
                <div className="stat-label" style={{ marginBottom: '0.4rem' }}>
                  <span style={{ marginRight: '0.3rem' }}>📅</span> {t('raffle.drawDate')}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--primary)' }}>
                    {new Date(raffle.drawDate).toLocaleDateString('pt-BR')}
                  </span>
                  {raffle.drawDate && raffle.drawDate.includes('T') && (
                    <span style={{
                      fontSize: '0.95rem', fontWeight: 600, color: 'var(--dark)',
                      background: 'linear-gradient(135deg, rgba(108,92,231,0.1), rgba(0,206,201,0.1))',
                      padding: '0.2rem 0.6rem', borderRadius: '20px',
                    }}>
                      🕐 {new Date(raffle.drawDate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {isOwner && (
              <div style={{ marginTop: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ fontWeight: 600 }}>Progresso</span>
                  <span style={{ color: 'var(--dark-light)' }}>{progressPercent.toFixed(0)}%</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${Math.min(progressPercent, 100)}%` }}></div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Numbers Section */}
        {canPurchase && (
          <div className="numbers-section">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>{raffle.numberType === 'unlimited' ? 'Comprar Bilhetes' : t('raffle.selectNumbers')}</h2>
              <button className="btn btn-accent btn-sm" onClick={handleShareRaffle} style={{ alignSelf: 'flex-start', whiteSpace: 'nowrap' }}>
                {t('raffle.shareCampaign')}
              </button>
            </div>

            {raffle.numberType === 'limited' ? (
              <>
                <div className="number-grid">
                  {Array.from({ length: maxNum }, (_, i) => raffle.zeroBased ? i : i + 1).map(number => (
                    <button
                      key={number}
                      className={`number-cell number-${numberStates[number]}`}
                      onClick={() => handleNumberClick(number)}
                      disabled={numberStates[number] === 'sold' || numberStates[number] === 'reserved'}
                    >
                      {number}
                    </button>
                  ))}
                </div>

                <div className="number-legend">
                  <div className="legend-item">
                    <div className="legend-dot" style={{ background: 'var(--gray-100)', border: '1px solid var(--gray-300)' }}></div>
                    <span>{t('raffle.available')}</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-dot" style={{ background: 'var(--primary)' }}></div>
                    <span>{t('raffle.selected')}</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-dot" style={{ background: 'var(--warning)' }}></div>
                    <span>{t('raffle.reserved')}</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-dot" style={{ background: 'var(--gray-300)' }}></div>
                    <span>{t('raffle.sold')}</span>
                  </div>
                </div>
              </>
            ) : (
              <div style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', padding: '2rem', boxShadow: 'var(--shadow-md)', marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Quantos bilhetes?
                </label>
                <input
                  type="number" min="1" max="100"
                  value={selectedNumbers.length || ''}
                  onChange={(e) => {
                    const count = Math.max(0, parseInt(e.target.value) || 0);
                    setSelectedNumbers(Array.from({ length: count }, (_, i) => raffle.zeroBased ? i : i + 1));
                  }}
                  placeholder="Quantidade de bilhetes"
                  style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)' }}
                />
              </div>
            )}
          </div>
        )}

        {/* Buy Section */}
        {canPurchase && (
          <div style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', padding: '2rem', boxShadow: 'var(--shadow-md)', marginTop: '2rem' }}>
            <h2 style={{ marginBottom: '1rem' }}>{t('raffle.myNumbers')}</h2>

            <div style={{ minHeight: '60px', padding: '1rem', background: 'var(--gray-100)', borderRadius: 'var(--radius-sm)', marginBottom: '1rem' }}>
              {selectedNumbers.length > 0 ? (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {selectedNumbers.sort((a, b) => a - b).map(number => (
                    <span key={number} style={{ background: 'var(--primary)', color: 'var(--white)', padding: '0.5rem 1rem', borderRadius: 'var(--radius-sm)', fontWeight: 600 }}>
                      {number}
                    </span>
                  ))}
                </div>
              ) : (
                <p style={{ color: 'var(--dark-light)', margin: 0 }}>Selecione os números acima para começar</p>
              )}
            </div>

            {selectedNumbers.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', background: 'var(--primary-light)', borderRadius: 'var(--radius-sm)', marginBottom: '1.5rem' }}>
                <span style={{ color: 'var(--white)', fontWeight: 600 }}>
                  {selectedNumbers.length} números x {fmtPrice} = <strong>{fmtTotal}</strong>
                </span>
              </div>
            )}

            <button
              className="btn btn-lg"
              onClick={handleBuyWithPix}
              disabled={selectedNumbers.length === 0 || purchasing}
              style={{
                width: '100%',
                background: selectedNumbers.length === 0 ? 'var(--gray-300)' : 'linear-gradient(135deg, #00CEC9, #00B894)',
                color: 'white', border: 'none', fontSize: '1.2rem', fontWeight: 700,
                padding: '1.1rem', borderRadius: 'var(--radius-md)',
                cursor: selectedNumbers.length === 0 ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              }}
            >
              <span style={{ fontSize: '1.4rem' }}>📲</span> Comprar com PIX
            </button>
          </div>
        )}
      </div>

      {/* Participant Legal Disclaimer */}
      <div style={{
        maxWidth: '900px', margin: '1.5rem auto 0', padding: '0 1rem',
      }}>
        <div style={{
          background: 'var(--gray-100, #f5f5f5)',
          borderRadius: '12px',
          padding: '1rem 1.25rem',
          fontSize: '0.8rem',
          color: 'var(--dark-light, #636e72)',
          lineHeight: 1.6,
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.75rem',
        }}>
          <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>ℹ️</span>
          <div>
            <p style={{ margin: 0 }}>
              {t('legal.participantDisclaimer')}{' '}
              <Link to="/terms" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>
                {t('footer.terms')}
              </Link>
            </p>
            {!isOwner && (
              <button
                onClick={() => setShowReportModal(true)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#d63031', fontSize: '0.8rem', fontWeight: 600,
                  padding: '0.4rem 0 0', textDecoration: 'underline',
                }}
              >
                {t('legal.reportCampaign')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Report Modal */}
      {showReportModal && (
        <div className="modal-overlay" onClick={() => setShowReportModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            {reportSent ? (
              <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
                <h3 style={{ margin: '0 0 0.5rem' }}>{t('legal.reportSuccess')}</h3>
              </div>
            ) : (
              <>
                <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>🚩</span> {t('legal.reportTitle')}
                </h2>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.9rem' }}>
                    {t('legal.reportReason')} *
                  </label>
                  <select
                    value={reportData.reason}
                    onChange={(e) => setReportData(prev => ({ ...prev, reason: e.target.value }))}
                    style={{
                      width: '100%', padding: '0.7rem', fontSize: '0.95rem',
                      border: '2px solid var(--gray-300)', borderRadius: '8px', boxSizing: 'border-box',
                    }}
                  >
                    <option value="">--</option>
                    <option value="fraud">{t('legal.reportReasonFraud')}</option>
                    <option value="illegal">{t('legal.reportReasonIllegal')}</option>
                    <option value="misleading">{t('legal.reportReasonMisleading')}</option>
                    <option value="other">{t('legal.reportReasonOther')}</option>
                  </select>
                </div>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.9rem' }}>
                    {t('legal.reportDetails')}
                  </label>
                  <textarea
                    value={reportData.details}
                    onChange={(e) => setReportData(prev => ({ ...prev, details: e.target.value }))}
                    rows={4}
                    style={{
                      width: '100%', padding: '0.7rem', fontSize: '0.95rem',
                      border: '2px solid var(--gray-300)', borderRadius: '8px',
                      resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button className="btn btn-secondary" onClick={() => setShowReportModal(false)} style={{ flex: 1 }}>
                    {t('common.cancel')}
                  </button>
                  <button
                    className="btn"
                    onClick={handleReportSubmit}
                    disabled={!reportData.reason || reportSubmitting}
                    style={{
                      flex: 1,
                      background: reportData.reason ? '#d63031' : 'var(--gray-300)',
                      color: '#fff', border: 'none', fontWeight: 600,
                      cursor: reportData.reason ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {reportSubmitting ? t('common.loading') : t('legal.reportSubmit')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* PIX Payment Modal */}
      {showPaymentModal && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>📲</span> Pagar com PIX
            </h2>

            <div className="payment-summary">
              <div className="payment-summary-row">
                <span>{selectedNumbers.length} números x {fmtPrice}</span>
                <span>{fmtTotal}</span>
              </div>
              <div className="payment-summary-row total">
                <span>Total</span>
                <span style={{ fontSize: '1.2rem' }}>{fmtTotal}</span>
              </div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, rgba(0,206,201,0.1), rgba(108,92,231,0.1))',
              borderRadius: 'var(--radius-md)', padding: '1.5rem', marginBottom: '1.5rem', textAlign: 'center'
            }}>
              <p style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.95rem' }}>Envie o PIX para:</p>
              <p style={{ fontSize: '0.8rem', color: 'var(--dark-light)', marginBottom: '0.8rem', textTransform: 'uppercase' }}>
                {{ cpf: 'CPF', cnpj: 'CNPJ', email: 'E-mail', phone: 'Telefone', random: 'Chave Aleatória', copiaecola: 'Copia e Cola', qrcode: 'QR Code' }[raffle.pixKeyType] || raffle.pixKeyType}: {raffle.createdByName}
              </p>

              {/* QR Code display */}
              {raffle.pixKeyType === 'qrcode' && raffle.pixQrCodeUrl ? (
                <div style={{ marginBottom: '0.8rem' }}>
                  <img
                    src={raffle.pixQrCodeUrl} alt="QR Code PIX"
                    style={{
                      maxWidth: '220px', maxHeight: '220px',
                      borderRadius: 'var(--radius-sm)',
                      border: '3px solid var(--primary)',
                      padding: '8px', background: 'white',
                    }}
                  />
                  <p style={{ fontSize: '0.8rem', color: 'var(--dark-light)', marginTop: '0.5rem' }}>
                    Escaneie o QR Code acima com o app do seu banco
                  </p>
                </div>
              ) : (
                <>
                  {/* Key / Copia e Cola display */}
                  <div style={{
                    background: 'var(--white)', border: '2px dashed var(--primary)',
                    borderRadius: 'var(--radius-sm)', padding: '1rem', fontFamily: 'monospace',
                    fontSize: raffle.pixKeyType === 'copiaecola' ? '0.75rem' : '1.1rem',
                    fontWeight: 700, color: 'var(--primary)',
                    marginBottom: '0.8rem', wordBreak: 'break-all',
                    maxHeight: raffle.pixKeyType === 'copiaecola' ? '120px' : 'none',
                    overflowY: raffle.pixKeyType === 'copiaecola' ? 'auto' : 'visible',
                    textAlign: 'left',
                  }}>
                    {raffle.pixKey}
                  </div>

                  <button className="btn btn-primary btn-sm" onClick={handleCopyPixKey}
                    style={{ background: pixCopied ? 'var(--success)' : 'var(--primary)', transition: 'all 0.3s' }}>
                    {pixCopied ? '✅ Copiado!' :
                     raffle.pixKeyType === 'copiaecola' ? '📋 Copiar Código PIX' : '📋 Copiar Chave PIX'}
                  </button>
                </>
              )}
            </div>

            <div style={{ marginBottom: '1.2rem', fontSize: '0.9rem', color: 'var(--dark-light)', lineHeight: 1.8 }}>
              <div><strong>1.</strong> Abra o app do seu banco</div>
              {raffle.pixKeyType === 'qrcode' ? (
                <div><strong>2.</strong> Escaneie o QR Code acima na opção PIX</div>
              ) : raffle.pixKeyType === 'copiaecola' ? (
                <div><strong>2.</strong> Vá em PIX &gt; Copia e Cola e cole o código acima</div>
              ) : (
                <div><strong>2.</strong> Vá em PIX e cole a chave acima</div>
              )}
              <div><strong>3.</strong> Envie <strong>{fmtTotal}</strong></div>
              <div><strong>4.</strong> Tire print/screenshot do comprovante</div>
              <div><strong>5.</strong> Envie o comprovante abaixo</div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.95rem' }}>
                📧 Seu E-mail *
              </label>
              <input
                type="email"
                value={buyerEmail}
                onChange={(e) => setBuyerEmail(e.target.value)}
                placeholder="seuemail@exemplo.com"
                required
                style={{
                  width: '100%', padding: '0.75rem', fontSize: '1rem',
                  border: '2px solid var(--gray-300)', borderRadius: 'var(--radius-sm)',
                  outline: 'none', transition: 'border 0.3s', boxSizing: 'border-box',
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--primary)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--gray-300)'}
              />
              <small style={{ color: 'var(--dark-light)', fontSize: '0.8rem' }}>
                Você receberá atualizações do pagamento neste e-mail
              </small>

              {!currentUser && (
                <div style={{ marginTop: '0.6rem' }}>
                  <button
                    type="button"
                    onClick={() => {
                      const currentPath = raffle.slug ? `/rifa/${raffle.slug}` : `/raffle/${raffleId}`;
                      navigate(`/login?returnTo=${encodeURIComponent(currentPath)}`);
                    }}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--primary)', fontWeight: 600, fontSize: '0.9rem',
                      padding: 0, textDecoration: 'underline',
                    }}
                  >
                    🔑 Já tenho login
                  </button>
                  <small style={{ color: 'var(--dark-light)', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                    — seu email será preenchido automaticamente
                  </small>
                </div>
              )}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.95rem' }}>
                📎 Comprovante de Pagamento *
              </label>

              <input
                type="file" ref={fileInputRef} onChange={handleComprovanteChange}
                accept="image/*,.pdf" style={{ display: 'none' }}
              />

              {!comprovanteFile ? (
                <button
                  type="button" onClick={() => fileInputRef.current?.click()}
                  style={{
                    width: '100%', padding: '1.5rem', border: '2px dashed var(--gray-300)',
                    borderRadius: 'var(--radius-md)', background: 'var(--gray-100)',
                    cursor: 'pointer', textAlign: 'center', fontSize: '0.95rem',
                    color: 'var(--dark-light)', transition: 'all 0.3s',
                  }}
                  onMouseOver={(e) => { e.target.style.borderColor = 'var(--primary)'; e.target.style.background = 'rgba(108,92,231,0.05)'; }}
                  onMouseOut={(e) => { e.target.style.borderColor = 'var(--gray-300)'; e.target.style.background = 'var(--gray-100)'; }}
                >
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📷</div>
                  <div>Clique para enviar o comprovante</div>
                  <div style={{ fontSize: '0.8rem', marginTop: '0.3rem' }}>Imagem ou PDF (máx 5MB)</div>
                </button>
              ) : (
                <div style={{
                  border: '2px solid var(--success)', borderRadius: 'var(--radius-md)',
                  padding: '1rem', background: 'rgba(0,184,148,0.05)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '1.5rem' }}>✅</span>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{comprovanteFile.name}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--dark-light)' }}>
                          {(comprovanteFile.size / 1024).toFixed(0)} KB
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => { setComprovanteFile(null); setComprovantePreview(null); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--danger)' }}
                    >
                      ✕
                    </button>
                  </div>
                  {comprovantePreview && comprovanteFile.type.startsWith('image/') && (
                    <img
                      src={comprovantePreview} alt="Comprovante"
                      style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: 'var(--radius-sm)', marginTop: '0.75rem' }}
                    />
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <button
                className="btn"
                onClick={handleConfirmPixSent}
                disabled={purchasing || !comprovanteFile || !buyerEmail}
                style={{
                  width: '100%',
                  padding: '0.85rem 1.5rem',
                  fontSize: '1rem',
                  background: (!comprovanteFile || !buyerEmail) ? 'var(--gray-300)' : 'linear-gradient(135deg, #00b894, #00cec9)',
                  color: 'white', border: 'none', fontWeight: 600, borderRadius: 'var(--radius-md)',
                  cursor: (!comprovanteFile || !buyerEmail) ? 'not-allowed' : 'pointer',
                }}
              >
                {purchasing ? 'Enviando...' : 'Enviar Comprovante'}
              </button>
              <button className="btn btn-secondary" onClick={() => setShowPaymentModal(false)} style={{ width: '100%', padding: '0.75rem 1.5rem', fontSize: '0.95rem' }}>
                Cancelar
              </button>
            </div>

            <p style={{ fontSize: '0.8rem', color: 'var(--dark-light)', textAlign: 'center', marginTop: '1rem' }}>
              Seus números ficarão reservados. O criador da rifa verificará seu comprovante e confirmará o pagamento.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
